import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { AppPageService, CustomerFacade, CustomerLoanStatus, LinkingStatus, ProductCode } from "common";
import { Observable, of } from "rxjs";
import { map, switchMap, take } from "rxjs/operators";
import { BankingService } from "../bank-account/banking.service";
import { FinancialInstitution } from "../bank-account/bank-account.model";
import { LoanFacade } from "../loan/domain/+state/loan.facade";
import { LoanData } from "../loan/domain/models/loan.model";
import { LoanFundedDialogComponent } from "../loan/loan-funded-dialog/loan-funded-dialog.component";
import { RenewalService } from "../renewal/renewal.service";

export interface PostSignNavigationInput {
  /** The loan the signature opened — the renewal loan on a renewal, not the loan that was signed from. */
  signedLoanId: number;
  /** The loan whose sign page the customer came from; the renewal offer bundle belongs to it. */
  sourceLoanId: number;
  /** The entity behind the loan, needed only for the bank-relink hand-off. */
  entityId: number;
  /** Set on a renewal, and the offer bundle to accept once the agreement is signed. */
  offerBundleId?: number;
  bankIntegrationRequired: boolean;
  /** Term-loan disbursed amount, shown in the funded dialog. */
  disbursedAmount?: number;
}

/** Where the customer goes once an agreement is signed. */
@Injectable({
  providedIn: "root",
})
export class PostSignNavigationService {
  constructor(
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly renewalService: RenewalService,
    private readonly loanFacade: LoanFacade,
    private readonly customerFacade: CustomerFacade,
    private readonly appPageService: AppPageService,
    private readonly bankingService: BankingService
  ) {}

  navigateAfterSigning$(input: PostSignNavigationInput): Observable<void> {
    if (input.offerBundleId) {
      this.renewalService.acceptOfferBundle(input.sourceLoanId, input.offerBundleId).subscribe();
    }

    if (this.appPageService.backToIfPossible(true)) {
      return of(undefined);
    }

    return this.loanFacade.refreshLoan(input.signedLoanId).pipe(
      take(1),
      switchMap((loan: LoanData) => this.routeForLoan$(loan, input)),
      map(() => undefined)
    );
  }

  private routeForLoan$(loan: LoanData, input: PostSignNavigationInput): Observable<unknown> {
    if (loan.productCode === ProductCode.Term && loan.customerFacingStatus === CustomerLoanStatus.Open) {
      return this.showTermLoanDraw$(loan, input);
    }

    if (loan.freezeForDays) {
      this.customerFacade.redirectToDashboard();
      return of(undefined);
    }

    void this.router.navigate([`account/${input.signedLoanId}/setup/make-a-draw`]);
    return of(undefined);
  }

  private showTermLoanDraw$(loan: LoanData, input: PostSignNavigationInput): Observable<unknown> {
    return LoanFundedDialogComponent.show(
      this.dialog,
      loan,
      input.bankIntegrationRequired,
      input.disbursedAmount
    ).pipe(
      take(1),
      map(() => {
        if (input.bankIntegrationRequired) {
          this.navigateToBankLinking(loan, input.entityId);
        } else {
          this.customerFacade.redirectToDashboard();
        }
      })
    );
  }

  private navigateToBankLinking(loan: LoanData, entityId: number): void {
    const bankAccount = loan.disbursementBankAccountData;

    const institution: FinancialInstitution = {
      institutionId: bankAccount?.syncedBankAccount?.institutionId,
      institutionLoginId: bankAccount?.syncedBankAccount?.institutionLoginId,
      institutionName: bankAccount?.syncedBankAccount?.institutionName ?? bankAccount?.bankName,
      routingNumber: bankAccount?.syncedBankAccount?.routingNumber ?? bankAccount?.routingNumber,
      isLinked: bankAccount?.linkingStatus === LinkingStatus.Linked,
    };

    this.bankingService.relink(entityId, institution, "dashboard");
  }
}
